import React, { useEffect, useRef, useState } from 'react';

import type { ImageProps } from './types';
import * as styles from './Image.styles';

/**
 * `Image` is used to render images that show a placeholder while they are loading, and provides a fallback for
 * lazy loading if the browser does not support it natively.
 *
 * `Image` is an **enhanced native element** component. That is, unlike other Ribbons components, where consumers should remain
 * agnostic about the HTML they render, with `Image`, consumers can rely on it to render an `<img />` element. As such it
 * supports all the props that a native `<img />` element supports.
 */
export const Image = ({
  alt,
  css,
  height,
  loading,
  onError,
  onLoad,
  src,
  width,
  ...restOfImgProps
}: ImageProps) => {
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);

  const onImageError = () => {
    setShowPlaceholder(false);
    onError?.();
  };

  const onImageLoad = () => {
    setShowPlaceholder(false);
    onLoad?.();
  };

  useEffect(() => {
    // If the image is already loaded (cached images), we need to trigger the onLoad event manually
    if (imgRef.current?.complete) {
      onImageLoad();
    }
  }, [src]);

  return (
    <img
      {...restOfImgProps}
      alt={alt}
      css={[showPlaceholder && styles.imagePlaceholder, css]}
      height={height}
      loading={loading}
      onError={onImageError}
      onLoad={onImageLoad}
      ref={imgRef}
      src={src}
      width={width}
    />
  );
};
